import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import bg from "./locales/bg.json";
import cs from "./locales/cs.json";
import hr from "./locales/hr.json";
import da from "./locales/da.json";
import nl from "./locales/nl.json";
import en from "./locales/en.json";
import et from "./locales/et.json";
import fi from "./locales/fi.json";
import fr from "./locales/fr.json";
import de from "./locales/de.json";
import el from "./locales/el.json";
import hu from "./locales/hu.json";
import ga from "./locales/ga.json";
import it from "./locales/it.json";
import lv from "./locales/lv.json";
import lt from "./locales/lt.json";
import mt from "./locales/mt.json";
import pl from "./locales/pl.json";
import pt from "./locales/pt.json";
import ro from "./locales/ro.json";
import sk from "./locales/sk.json";
import sl from "./locales/sl.json";
import es from "./locales/es.json";
import sv from "./locales/sv.json";

// Convention: t("namespace.key"). Add new strings to locales/it.json (base) AND every other locale;
// `yarn i18n:check` fails on any key-parity mismatch. Never hardcode UI text in components.
export const LANGUAGES: { code: string; name: string }[] = [
  { code: "bg", name: "Български" },
  { code: "cs", name: "Čeština" },
  { code: "hr", name: "Hrvatski" },
  { code: "da", name: "Dansk" },
  { code: "nl", name: "Nederlands" },
  { code: "en", name: "English" },
  { code: "et", name: "Eesti" },
  { code: "fi", name: "Suomi" },
  { code: "fr", name: "Français" },
  { code: "de", name: "Deutsch" },
  { code: "el", name: "Ελληνικά" },
  { code: "hu", name: "Magyar" },
  { code: "ga", name: "Gaeilge" },
  { code: "it", name: "Italiano" },
  { code: "lv", name: "Latviešu" },
  { code: "lt", name: "Lietuvių" },
  { code: "mt", name: "Malti" },
  { code: "pl", name: "Polski" },
  { code: "pt", name: "Português" },
  { code: "ro", name: "Română" },
  { code: "sk", name: "Slovenčina" },
  { code: "sl", name: "Slovenščina" },
  { code: "es", name: "Español" },
  { code: "sv", name: "Svenska" },
];

export const DEFAULT_LANGUAGE = "it";
export const LANG_STORAGE_KEY = "ss_lang";
const CODES = new Set(LANGUAGES.map((l) => l.code));

export const isSupportedLanguage = (code: unknown): code is string => typeof code === "string" && CODES.has(code);

export function storedLanguage(): string {
  try {
    const v = localStorage.getItem(LANG_STORAGE_KEY);
    return isSupportedLanguage(v) ? v : DEFAULT_LANGUAGE;
  } catch {
    return DEFAULT_LANGUAGE;
  }
}

i18n.use(initReactI18next).init({
  resources: {
    bg: { translation: bg }, cs: { translation: cs }, hr: { translation: hr }, da: { translation: da },
    nl: { translation: nl }, en: { translation: en }, et: { translation: et }, fi: { translation: fi },
    fr: { translation: fr }, de: { translation: de }, el: { translation: el }, hu: { translation: hu },
    ga: { translation: ga }, it: { translation: it }, lv: { translation: lv }, lt: { translation: lt },
    mt: { translation: mt }, pl: { translation: pl }, pt: { translation: pt }, ro: { translation: ro },
    sk: { translation: sk }, sl: { translation: sl }, es: { translation: es }, sv: { translation: sv },
  },
  lng: storedLanguage(),
  fallbackLng: DEFAULT_LANGUAGE,
  supportedLngs: LANGUAGES.map((l) => l.code),
  interpolation: { escapeValue: false },
  returnNull: false,
  returnEmptyString: false,
  saveMissing: process.env.NODE_ENV === "development",
  missingKeyHandler: (lngs, _ns, key) => {
    if (process.env.NODE_ENV === "development") console.warn(`[i18n] missing key "${key}" for ${lngs.join(",")}`);
  },
});

i18n.on("languageChanged", (lng) => {
  document.documentElement.lang = lng;
});
document.documentElement.lang = i18n.language;

// Typed helper for non-React modules (lib/*.ts, class components).
export const tr = (key: string, opts?: Record<string, unknown>): string => String((i18n.t as unknown as (k: string, o?: Record<string, unknown>) => string)(key, opts));

export function applyLanguage(code: string) {
  if (!isSupportedLanguage(code) || code === i18n.language) return;
  try {
    localStorage.setItem(LANG_STORAGE_KEY, code);
  } catch {
    /* storage unavailable */
  }
  void i18n.changeLanguage(code);
}

export default i18n;
